<template>
  <sfs-base-table
    ref="table"
    :data-provider="$invoicesAPI.getInvoices"
    provider-key="invoices"
    :columns="displayColumns"
    :filters="filters"
    :current-page="currentPage"
    :per-page="perPage"
    :show-per-page="showPerPage"
    @data-changed="onRecordsChanged"
  >
    <template #head(checkbox)>
      <b-form-checkbox
        v-if="selectableInvoices.length"
        class="checkbox-header"
        :checked="isAllSelected"
        @change="isAllSelected ? clearAllRows() : selectAllRows()"
      />
    </template>
    <template #cell(checkbox)="data">
      <b-form-checkbox
        v-if="isSelectableInvoice(data.item)"
        class="checkbox"
        v-model="selectedInvoices"
        :value="data.item"
      />
    </template>
    <template #cell(number)="data">
      <b-link :to="{ name: 'invoice', params: { id: data.item.id } }">
        {{ data.item.number }}
      </b-link>
      <div class="font-size-sm text-secondary">
        <strong>ID: {{ data.item.id }}</strong>
      </div>
    </template>
    <template #cell(client_id)="data">
      <b-link :to="{ name: 'client', params: { id: data.item.client.id } }">
        {{ data.item.client.name }}
      </b-link>
    </template>
    <template #cell(date)="data">
      {{ data.item.date | dateFormat }}
    </template>
    <template #cell(due_date)="data">
      {{ data.item.due_date | dateFormat }}
    </template>
    <template #cell(subtotal)="data">
      <money-format
        :value="data.item.subtotal.amount"
        :currency-code="data.item.subtotal.currency"
        subunits-value
      />
    </template>
    <template #cell(taxes)="data">
      <money-format
        :value="data.item.taxes.amount"
        :currency-code="data.item.taxes.currency"
        subunits-value
      />
    </template>
    <template #cell(balance)="data">
      <money-format
        :value="data.item.balance.amount"
        :currency-code="data.item.balance.currency"
        subunits-value
      />
    </template>
    <template #cell(status)="data">
      <b-badge
        v-if="data.item.finalized_at"
        class="mr-2"
        variant="success"
      >
        Finalized
      </b-badge>
      <b-badge
        v-else
        class="mr-2"
        variant="secondary"
      >
        Open
      </b-badge>
    </template>
    <template #cell(emailed)="data">
      <b-badge
        class="mr-2"
        :variant="deliveryStatus(data.item).variant"
      >
        {{ deliveryStatus(data.item).label }}
      </b-badge>
    </template>
  </sfs-base-table>
</template>

<script>
import tablePropsMixin from '@/mixins/tablePropsMixin';
import translationMixin from '@/mixins/translationMixin';
import { INVOICE_TABLE_DEFAULT_COLUMNS, INVOICE_MODEL_NAME, PORTAL_INVOICE_DELIVERY_METHODS } from '@/constants/invoices';

export default {
  name: 'InvoicesTable',
  mixins: [tablePropsMixin, translationMixin],
  props: {
    columns: {
      type: Array,
      default: () => INVOICE_TABLE_DEFAULT_COLUMNS,
    },
  },
  data() {
    return {
      modelName: INVOICE_MODEL_NAME,
      selectedInvoices: [],
      records: [],
    };
  },
  computed: {
    portalDeliveryFiltered() {
      return PORTAL_INVOICE_DELIVERY_METHODS.includes(this.filters?.invoice_delivery_method);
    },
    showSelectionColumn() {
      return this.portalDeliveryFiltered || this.records.some((invoice) => this.isPortalInvoice(invoice));
    },
    tableColumns() {
      if (!this.showSelectionColumn) {
        return this.columns;
      }
      const checkboxColumn = { key: 'checkbox', label: '' };
      const hasCheckbox = this.columns?.some((column) => column.key === checkboxColumn.key);
      return hasCheckbox ? this.columns : [checkboxColumn, ...this.columns];
    },
    displayColumns() {
      return this.translatedColumns(this.modelName, this.tableColumns).map((column) => {
        if (column.key === 'emailed' && this.portalDeliveryFiltered) {
          return { ...column, label: 'Uploaded' };
        }
        return column;
      });
    },
    selectableInvoices() {
      return this.records.filter((invoice) => this.isSelectableInvoice(invoice));
    },
    isAllSelected() {
      return this.selectableInvoices.length > 0 && this.selectedInvoices.length === this.selectableInvoices.length;
    },
  },
  watch: {
    selectedInvoices(newValue) {
      this.$emit('selected-changed', newValue);
    },
  },
  methods: {
    isPortalInvoice(invoice) {
      return PORTAL_INVOICE_DELIVERY_METHODS.includes(invoice.client?.invoice_delivery_method);
    },
    isSelectableInvoice(invoice) {
      return this.isPortalInvoice(invoice) && !invoice.uploaded_at && this.$can('update', invoice);
    },
    deliveryStatus(invoice) {
      if (this.isPortalInvoice(invoice)) {
        return invoice.uploaded_at
          ? { label: 'Uploaded', variant: 'success' }
          : { label: 'Not Uploaded', variant: 'secondary' };
      }

      return invoice.emailed_at
        ? { label: 'Emailed', variant: 'success' }
        : { label: 'Not Emailed', variant: 'secondary' };
    },
    onRecordsChanged(records) {
      this.records = records || [];
      if (this.selectedInvoices.length) {
        this.clearAllRows();
      }
    },
    selectAllRows() {
      this.selectedInvoices = [...this.selectableInvoices];
    },
    clearAllRows() {
      this.selectedInvoices = [];
    },
    clearSelection() {
      this.clearAllRows();
    },
    refresh() {
      this.clearAllRows();
      this.$refs.table.getRecords();
    },
  },
};
</script>
